import { APIResponse, apiService } from "../api";
import axiosInstance from "../api/axiosInstance";

// Enums from backend
export enum EventTypeEnum {
  CONFERENCE = "Conference",
  WORKSHOP = "Workshop",
  PRODUCT_LAUNCH = "Product Launch",
  EXHIBITION = "Exhibition",
  CORPORATE_MEETING = "Corporate Meeting",
  PRIVATE_EVENT = "Private Event",
  GOVERNMENT_EVENT = "Government Event",
  INTERNAL_COMPANY_EVENT = "Internal Company Event",
}

export enum AudienceTypeEnum {
  VIP = "VIP",
  EMPLOYEES = "Employees",
  CLIENTS = "Clients",
  PUBLIC = "Public",
}

export enum EventStatus {
  UPCOMING = "UPCOMING",
  ONGOING = "ONGOING",
  COMPLETED = "COMPLETED",
  CANCELLED = "CANCELLED",
}

export enum EventType {
  INTERNAL = "INTERNAL",
  EXTERNAL = "EXTERNAL",
}

// Backend API Response Interfaces
export interface EventLocation {
  type: "internal" | "external";
  room_name?: string;
  room_id?: string;
  address?: string;
}

export interface BackendEvent {
  id: string;
  code: string;
  title: string;
  description: string;
  cover_image: string;
  start_date_time: string;
  end_date_time: string;
  event_type: EventType;
  status: EventStatus;
  is_public: boolean;
  location: EventLocation;
  participation_limit: number;
  participation_count: number;
  spots_available: number;
  is_full: boolean;
  created_at: string;
  images?: string[];
  cover_image_index?: number;
  going_count?: number;
  not_interested_count?: number;
  updated_at?: string;
}

// Events API Response (List)
export interface EventsListResponse {
  success: boolean;
  message: string;
  data: BackendEvent[];
  meta: {
    total: number;
    page: number;
    limit: number;
    total_pages: number;
  };
}

// Events API Response (Single)
export interface SingleEventResponse {
  success: boolean;
  message: string;
  data: BackendEvent;
}

export interface EventsQueryParams {
  page?: number;
  limit?: number;
  status?: EventStatus;
  event_type?: EventType;
  is_public?: boolean;
}

// Event Request Interface
export interface EventRequest {
  full_name: string;
  email: string;
  mobile_number: string;
  company_name: string;
  event_type: EventTypeEnum;
  event_name: string;
  event_date_start: string; // YYYY-MM-DD
  event_date_end: string; // YYYY-MM-DD
  event_duration_from: string; // HH:MM
  event_duration_to: string; // HH:MM
  expected_attendees: number;
  audience_type: AudienceTypeEnum;
  required_services: string;
}

// Display Event Interface (transformed from backend)
export interface Event {
  id: string;
  event_name: string;
  event_type: string;
  event_date_start: string;
  event_date_end: string;
  event_duration_from: string;
  event_duration_to: string;
  expected_attendees: number;
  audience_type: string;
  description?: string;
  cover_image?: string;
  location?: EventLocation;
  status?: EventStatus;
  is_public?: boolean;
  participation_limit?: number;
  participation_count?: number;
  spots_available?: number;
  is_full?: boolean;
  images?: string[];
  going_count?: number;
  not_interested_count?: number;
}

class EventService {
  private readonly requestBaseUrl = "/event_requests/v1/landing";
  private readonly eventsBaseUrl = "/events/v1/landing";

  // Get all events (raw - returns array directly)
  async getEvents(params?: EventsQueryParams): Promise<BackendEvent[]> {
    const response = await axiosInstance.get<EventsListResponse>(
      this.eventsBaseUrl,
      {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 50,
          ...(params?.status && { status: params.status }),
          ...(params?.event_type && { event_type: params.event_type }),
          ...(params?.is_public !== undefined && {
            is_public: params.is_public,
          }),
        },
      },
    );
    return response.data.data;
  }

  // Get all events (formatted for useAPI hook - returns APIResponse format)
  async getEventsForHook(
    params?: EventsQueryParams,
  ): Promise<APIResponse<BackendEvent>> {
    const response = await axiosInstance.get<EventsListResponse>(
      this.eventsBaseUrl,
      {
        params: {
          page: params?.page || 1,
          limit: params?.limit || 50,
          ...(params?.status && { status: params.status }),
          ...(params?.event_type && { event_type: params.event_type }),
          ...(params?.is_public !== undefined && {
            is_public: params.is_public,
          }),
        },
      },
    );
    // Transform to APIResponse format (with rows and count)
    return {
      rows: response.data.data,
      count: response.data.meta.total,
    };
  }

  // Get single event by ID (using axiosInstance directly)
  async getEventById(id: string): Promise<BackendEvent> {
    const response = await axiosInstance.get<SingleEventResponse>(
      `${this.eventsBaseUrl}/${id}`,
    );
    return response.data.data;
  }

  // Transform backend event to display event
  transformBackendEvent(backendEvent: BackendEvent): Event {
    const startDateTime = new Date(backendEvent.start_date_time);
    const endDateTime = new Date(backendEvent.end_date_time);

    return {
      id: backendEvent.id,
      event_name: backendEvent.title,
      event_type: backendEvent.event_type,
      event_date_start: startDateTime.toISOString().split("T")[0],
      event_date_end: endDateTime.toISOString().split("T")[0],
      event_duration_from: startDateTime.toTimeString().slice(0, 5),
      event_duration_to: endDateTime.toTimeString().slice(0, 5),
      expected_attendees: backendEvent.participation_limit,
      audience_type: backendEvent.is_public ? "Public" : "Private",
      description: backendEvent.description,
      cover_image: backendEvent.cover_image,
      location: backendEvent.location,
      status: backendEvent.status,
      is_public: backendEvent.is_public,
      participation_limit: backendEvent.participation_limit,
      participation_count: backendEvent.participation_count,
      spots_available: backendEvent.spots_available,
      is_full: backendEvent.is_full,
      images: backendEvent.images,
      going_count: backendEvent.going_count,
      not_interested_count: backendEvent.not_interested_count,
    };
  }

  // Submit event request
  async createEventRequest(data: EventRequest): Promise<APIResponse<any>> {
    return apiService.post<any>(this.requestBaseUrl, data);
  }

  // Helper to format phone number (Saudi format)
  formatPhoneNumber(phone: string): string {
    let cleaned = phone.replace(/\D/g, "");
    if (cleaned.startsWith("966")) {
      cleaned = cleaned.substring(3);
    }
    if (cleaned.startsWith("0")) {
      cleaned = cleaned.substring(1);
    }
    return `+966${cleaned}`;
  }

  // Get event type options for dropdown
  getEventTypeOptions() {
    return Object.values(EventTypeEnum).map((type) => ({
      value: type,
      label: type,
    }));
  }

  // Get audience type options for dropdown
  getAudienceTypeOptions() {
    return Object.values(AudienceTypeEnum).map((type) => ({
      value: type,
      label: type,
    }));
  }
}

const eventService = new EventService();
export default eventService;

// import { APIResponse, apiService } from "../api";

// // Enums from backend
// export enum EventTypeEnum {
//   CONFERENCE = "Conference",
//   WORKSHOP = "Workshop",
//   PRODUCT_LAUNCH = "Product Launch",
//   EXHIBITION = "Exhibition",
//   CORPORATE_MEETING = "Corporate Meeting",
//   PRIVATE_EVENT = "Private Event",
//   GOVERNMENT_EVENT = "Government Event",
//   INTERNAL_COMPANY_EVENT = "Internal Company Event",
// }

// export enum AudienceTypeEnum {
//   VIP = "VIP",
//   EMPLOYEES = "Employees",
//   CLIENTS = "Clients",
//   PUBLIC = "Public",
// }

// // Event Request Interface
// export interface EventRequest {
//   full_name: string;
//   email: string;
//   mobile_number: string;
//   company_name: string;
//   event_type: EventTypeEnum;
//   event_name: string;
//   event_date_start: string; // YYYY-MM-DD
//   event_date_end: string; // YYYY-MM-DD
//   event_duration_from: string; // HH:MM
//   event_duration_to: string; // HH:MM
//   expected_attendees: number;
//   audience_type: AudienceTypeEnum;
//   required_services: string;
// }

// // Static Event Interface (for display)
// export interface Event {
//   id: string;
//   event_name: string;
//   event_type: EventTypeEnum;
//   event_date_start: string;
//   event_date_end: string;
//   event_duration_from: string;
//   event_duration_to: string;
//   expected_attendees: number;
//   audience_type: AudienceTypeEnum;
// }

// class EventService {
//   private readonly baseUrl = "/event_requests/v1/landing";

//   // Submit event request
//   async createEventRequest(data: EventRequest): Promise<APIResponse<any>> {
//     return apiService.post<any>(this.baseUrl, data);
//   }

//   // Helper to format phone number (Saudi format)
//   formatPhoneNumber(phone: string): string {
//     let cleaned = phone.replace(/\D/g, "");
//     if (cleaned.startsWith("966")) {
//       cleaned = cleaned.substring(3);
//     }
//     if (cleaned.startsWith("0")) {
//       cleaned = cleaned.substring(1);
//     }
//     return `+966${cleaned}`;
//   }

//   // Get event type options for dropdown
//   getEventTypeOptions() {
//     return Object.values(EventTypeEnum).map((type) => ({
//       value: type,
//       label: type,
//     }));
//   }

//   // Get audience type options for dropdown
//   getAudienceTypeOptions() {
//     return Object.values(AudienceTypeEnum).map((type) => ({
//       value: type,
//       label: type,
//     }));
//   }
// }

// const eventService = new EventService();
// export default eventService;
